import { faCheck } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import clsx from 'clsx'
import { usePostHog } from 'posthog-js/react'
import type { ReactElement } from 'react'
import { useCallback, useContext, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { z } from 'zod'
import {
  type DataTableRowSelection,
  type DataTableSort,
  useDataTableSearchParams
} from '~/components/DataTable/DataTable'
import { FilterBar } from '~/components/Filters/FilterBar/FilterBar'
import type { FiltersState } from '~/components/Filters/FilterBar/types'
import { ButtonGroup } from '~/components/Global/Blocks/ButtonGroup/ButtonGroup'
import { Paper } from '~/components/Global/Blocks/Paper/Paper'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import { Button } from '~/components/Global/Interactions/Button/Button'
import { ButtonVariation } from '~/components/Global/Interactions/Button/ButtonVariation'
import { captureBulkProcessOpened } from '~/domains/applications/analytics'
import { ApplicationModal } from '~/domains/applications/components/ApplicationModal'
import { ApplicationsTable } from '~/domains/applications/components/ApplicationsTable'
import { BulkApplicationModal } from '~/domains/applications/components/BulkApplicationModal'
import { BulkProcessActionButtons } from '~/domains/applications/components/BulkProcessActionButtons'
import type { BulkProcessAction } from '~/domains/applications/mutations/bulkProcessApplications'
import { useApplicationsList } from '~/domains/applications/queries/applicationsList'
import type { ApplicationCollection } from '~/domains/applications/types'
import { useUpdateConfig } from '~/domains/configuration/mutations/updateConfig'
import { useConfig } from '~/domains/configuration/queries/config'
import {
  contentWrapper,
  contentWrapperWide
} from '~/pages/styles/Dashboard.css'
import { listWrapper, quickControlsBar } from '~/pages/styles/Orders.css'
import { AuthContext } from '~/utils/contextes'
import useDocumentTitle from '~/utils/documentTitle'
import { urlNumber } from '~/utils/zod'
import { useApplicationFilterDefinitions } from './-components/applicationFilterDefinitions'
import { Header } from './-components/Header'

const searchSchema = z
  .object({
    page: urlNumber,
    sort: z.string().optional(),
    open: urlNumber
  })
  .catchall(z.string())

const defaultSorting: DataTableSort[] = [{ id: 'receivedAt', desc: true }]

const ApplicationsShowcase = (): ReactElement => {
  useDocumentTitle('Candidatures')
  const posthog = usePostHog()
  const currentUser = useContext(AuthContext)
  const filterDefinitions = useApplicationFilterDefinitions()
  const { data: jobmailCurrentUserFilter } = useConfig(
    'listing.jobmail_current_user_filter'
  )
  const { data: isDefaultPage } = useConfig('jobmail.applications_default_page')
  const { mutate: setDefaultApplicationsPage } = useUpdateConfig(
    'jobmail.applications_default_page'
  )

  const search = useSearch({ from: '/_dashboard/applications/' })
  const navigate = useNavigate({ from: '/applications/' })

  // `open` drives the modal, not the listing: parseDataTableSearch turns any unknown key into a panel filter
  const { open: openApplicationId, ...tableSearch } = search

  const navigateSearch = useCallback(
    (opts: { search: Record<string, string>; replace?: boolean }) => {
      void navigate({
        search: opts.search,
        replace: opts.replace
      })
    },
    [navigate]
  )

  const {
    queryParams,
    setQueryParams,
    panelFilters,
    setPanelFilters,
    resetAllFilters
  } = useDataTableSearchParams({
    search: tableSearch,
    navigate: navigateSearch,
    defaultSorting
  })

  const [selectedApplicationId, setSelectedApplicationId] = useState<
    number | null
  >(null)
  // Frozen while the modal is open: processed applications leave the refetched listing, which would
  // otherwise lose the current position and break the previous/next navigation
  const [navigationCollection, setNavigationCollection] =
    useState<ApplicationCollection[]>()
  const [rowSelection, setRowSelection] = useState<DataTableRowSelection>({})
  const [bulkApplicationIds, setBulkApplicationIds] = useState<number[]>([])
  const [bulkAction, setBulkAction] = useState<BulkProcessAction>('decline')

  const { data, totalItems, isLoading, isFetching, refetch } =
    useApplicationsList(queryParams, panelFilters, filterDefinitions)

  // Consumed once so a refresh or a back navigation does not reopen the modal
  useEffect(() => {
    if (openApplicationId === undefined) {
      return
    }
    setSelectedApplicationId(openApplicationId)
    void navigate({
      search: ({ open, ...previous }) => previous,
      replace: true
    })
  }, [openApplicationId, navigate])

  if (jobmailCurrentUserFilter === undefined) {
    return <Loader />
  }

  const defaultFilters: FiltersState = {
    status: 'untreated',
    archivedOffer: 'exclude'
  }
  if (jobmailCurrentUserFilter && currentUser) {
    defaultFilters.users = String(currentUser.id)
  }

  const hasActiveFilters =
    queryParams.columnFilters.length > 0 || Object.keys(panelFilters).length > 0

  const appWrapper = document.querySelector('#appWrapper')

  return (
    <div className={clsx(contentWrapper, contentWrapperWide)}>
      <Header />
      {isDefaultPage !== 'v3' && (
        <Alert variation={AlertVariation.Primary} onDismiss={false}>
          <ButtonGroup align="spaceBetween">
            Bienvenue sur la nouvelle page de traitement des candidatures — vos
            retours nous aident à l&apos;améliorer.
            <Button
              buttonSize="small"
              variation={ButtonVariation.PrimaryInversed}
              onClick={() => {
                setDefaultApplicationsPage({ value: 'v3' })
              }}
            >
              <FontAwesomeIcon icon={faCheck} />
              Définir comme page par défaut
            </Button>
          </ButtonGroup>
        </Alert>
      )}
      <Paper className={listWrapper}>
        <div className={quickControlsBar}>
          <FilterBar
            definitions={filterDefinitions}
            filters={panelFilters}
            onChange={setPanelFilters}
            savedSearchesKey="search.saved_applications"
            defaultFilters={defaultFilters}
            analyticsKey="applications"
          />
        </div>
        <ApplicationsTable
          data={data}
          totalItems={totalItems}
          queryParams={queryParams}
          onQueryChange={setQueryParams}
          onResetFilters={resetAllFilters}
          showResetFilters={hasActiveFilters}
          onOpenApplication={(application) => {
            setSelectedApplicationId(application.id)
            setNavigationCollection(data)
          }}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          bulkActions={(selectedIds) => (
            <BulkProcessActionButtons
              variation={ButtonVariation.PrimaryInversed}
              onSelect={(action) => {
                captureBulkProcessOpened(posthog, action, selectedIds.length)
                setBulkAction(action)
                setBulkApplicationIds(selectedIds.map(Number))
              }}
            />
          )}
          isLoading={isLoading}
          isFetching={isFetching}
          onRefresh={() => void refetch()}
        />
      </Paper>

      {selectedApplicationId !== null &&
        appWrapper instanceof HTMLElement &&
        createPortal(
          <ApplicationModal
            key="application-modal"
            defaultApplicationId={selectedApplicationId}
            collection={navigationCollection}
            showOrder
            onClose={() => {
              setSelectedApplicationId(null)
              setNavigationCollection(undefined)
            }}
          />,
          appWrapper
        )}

      {bulkApplicationIds.length > 0 &&
        appWrapper &&
        createPortal(
          <BulkApplicationModal
            key="bulk-application-modal"
            applicationIds={bulkApplicationIds}
            action={bulkAction}
            onClose={() => setBulkApplicationIds([])}
            onProcessed={() => {
              setBulkApplicationIds([])
              setRowSelection({})
            }}
          />,
          appWrapper
        )}
    </div>
  )
}

export const Route = createFileRoute('/_dashboard/applications/')({
  validateSearch: searchSchema,
  component: ApplicationsShowcase
})
